import React, { useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import moment from "moment";

import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { change } from "store/root/actions";
import { CodeDrawer as CodeDrawerForm, standardCodeFormName, uDefCodeFormName } from "utils/codeLib";
import { useStateSelector } from "utils/field";

import Table from "components/Table";
import Drawer from "components/Drawer";
import { headCells, rows } from "../CodeList";

import classes from "./style.module.scss";
import codeListClasses from "../CodeList/style.module.scss";

import EditIcon from "assets/images/icons/JSX/Edit";

const { clearInitCode, setInitCode } = enclosureActions;

const filterCodesBySearch = (codes, searchField) => {
    const query = searchField.toLowerCase();

    return Object.fromEntries(
        Object.entries(codes).filter(([, code]) => (code.label || "").toLowerCase().includes(query))
    );
};

const getSearchStats = (tabs, codeLib, searchField) => {
    let totalCount = 0;
    let standardCount = 0;
    let uDefCount = 0;

    tabs.forEach(({ type }) => {
        const filteredCodes = filterCodesBySearch(codeLib[type]?.codes || {}, searchField);
        const codeRefs = Object.keys(filteredCodes);

        totalCount += codeRefs.length;
        standardCount += codeRefs.filter((codeRef) => codeRef.split(`${type}-U`)[0]).length;
        uDefCount += codeRefs.filter((codeRef) => !codeRef.split(`${type}-U`)[0]).length;
    });

    return { totalCount, standardCount, uDefCount };
};

const CodeSearchView = ({ searchField, tabs, rValueUnits, clearInitCode, setInitCode, change }) => {
    const [editOpen, toggleEditOpen] = useState("");
    const [componentType, toggleComponentType] = useState("");
    const [codeType, toggleCodeType] = useState("S");
    const [editName, setEditName] = useState("");

    const codeLib = useStateSelector(`user.codeLib`) || {};
    const calculatingRValue = useStateSelector(`enclosure.calculatingRValue.codeLib`) || {};

    const { totalCount, standardCount, uDefCount } = useMemo(
        () => getSearchStats(tabs, codeLib, searchField),
        [tabs, codeLib, searchField]
    );

    const matchingSections = useMemo(
        () =>
            tabs
                .map(({ name, type }) => ({
                    name,
                    type,
                    codes: filterCodesBySearch(codeLib[type]?.codes || {}, searchField),
                }))
                .filter(({ codes }) => Object.keys(codes).length > 0),
        [tabs, codeLib, searchField]
    );

    const editCodes = codeLib[componentType]?.codes || {};

    const handleEditClose = useCallback(() => {
        toggleEditOpen("");
        toggleCodeType("S");
        setEditName("");
        clearInitCode();
    }, [clearInitCode]);

    const handleEditOpen = useCallback(
        async (id, code, editComponentType, editCodeType) => {
            const codeName =
                editCodeType === "S" ? standardCodeFormName(editComponentType) : uDefCodeFormName(editComponentType);
            const sectionName = tabs.find(({ type }) => type === editComponentType)?.name || "";

            toggleComponentType(editComponentType);
            toggleCodeType(editCodeType);
            setEditName(sectionName);
            await setInitCode(code, codeName);
            toggleEditOpen(id);
        },
        [setInitCode, tabs]
    );

    const handleUdefSaveEdit = useCallback(() => {
        toggleCodeType("S");
        toggleEditOpen(false);
    }, []);

    return (
        <div className={classes.codeSearchView}>
            <div className={classes.topWrapper}>
                <h2>Search Results</h2>
                <div className={classes.stats}>
                    <div className={`${classes.stat} ${classes.largeStat}`}>
                        <div>
                            <span className={classes.name}>Total Codes</span>
                            <span className={classes.value}>{totalCount}</span>
                        </div>
                    </div>
                    <div className={classes.stat}>
                        <div>
                            <span className={classes.name}>Standard Codes</span>
                            <span className={classes.value}>
                                {standardCount}
                                <small> / {totalCount}</small>
                            </span>
                        </div>
                    </div>
                    <div className={classes.stat}>
                        <div>
                            <span className={classes.name}>User Defined Codes</span>
                            <span className={classes.value}>
                                {uDefCount}
                                <small> / {totalCount}</small>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {matchingSections.length === 0 ? (
                <div className={classes.zeroState}>
                    <p>No codes match your search</p>
                </div>
            ) : (
                matchingSections.map(({ name, type, codes }) => (
                    <div key={type} className={classes.typeSection}>
                        <h3>{name}</h3>
                        <div className={classes.tableWrapper}>
                            <Table
                                paginate
                                defaultRowsPerPage={10}
                                headCells={headCells(type)}
                                defaultSortProp="lastEdited"
                                rows={rows(
                                    type,
                                    codes,
                                    calculatingRValue,
                                    toggleComponentType,
                                    toggleCodeType,
                                    toggleEditOpen,
                                    handleEditOpen,
                                    rValueUnits
                                )}
                            />
                        </div>
                    </div>
                ))
            )}

            <Drawer
                open={!!editOpen}
                title={`Edit ${editName} Code`}
                subtitle={
                    <span>
                        Last Modified:{" "}
                        {editCodes[editOpen] && editCodes[editOpen].lastEdited
                            ? moment(editCodes[editOpen].lastEdited).format("MMM DD, YYYY")
                            : "-"}
                        <br />
                        Editing a library code will <strong>not</strong> update instances of the code already used in
                        models.
                    </span>
                }
                close={handleEditClose}
                icon={() => <EditIcon fill="#0b91ea" />}
                headerClassName={codeListClasses.editHeader}
                introColor="#f0f8fd"
            >
                <CodeDrawerForm
                    toggleOpen={toggleEditOpen}
                    change={change}
                    fieldAccessor={"none"}
                    componentType={componentType}
                    codeType={codeType}
                    handleClose={handleEditClose}
                    isCodeLibrary
                    isFlat={componentType === "CeilingFlat"}
                    handleUdefSave={handleUdefSaveEdit}
                />
            </Drawer>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => {
        dispatch(clearInitCode());
    },
    setInitCode: (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    change: (path, value) => dispatch(change(path, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CodeSearchView);
